import { useCallback, useEffect, useRef, useState } from 'react'
import { getAuthHeaders } from '../utils/auth.js'
import Button from '../design-system/primitives/Button.jsx'
import { DataPanelCard, StatusPill } from '../design-system/patterns/index.js'
import DocumentsDeleteModal from './admin/documents/DocumentsDeleteModal.jsx'
import {
  DOC_ICON_MAP,
  docKindFromName,
  formatBytes,
  statusMessageText,
  statusMessageVariant,
} from './admin/documents/documentsAdminUtils.js'

export default function DocumentsAdmin() {
  const inputRef = useRef(null)
  const [pendingFiles, setPendingFiles] = useState([])
  const [library, setLibrary] = useState([])
  const [libraryLoading, setLibraryLoading] = useState(true)
  const [libraryError, setLibraryError] = useState('')
  const [sortDesc, setSortDesc] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [status, setStatus] = useState('')
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const loadLibrary = useCallback(async () => {
    setLibraryLoading(true)
    setLibraryError('')
    try {
      const res = await fetch('/rag/files', { headers: getAuthHeaders() })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`)
      const rows = Array.isArray(data.files) ? data.files : []
      const normalized = rows.map((item) =>
        typeof item === 'string' ? { name: item, size_bytes: null, modified: null } : item
      )
      setLibrary(normalized)
    } catch (e) {
      setLibraryError(e.message || 'Could not load document library')
      setLibrary([])
    } finally {
      setLibraryLoading(false)
    }
  }, [])

  useEffect(() => {
    loadLibrary()
  }, [loadLibrary])

  const sortedLibrary = [...library].sort((a, b) => {
    const an = (a.name || '').toLowerCase()
    const bn = (b.name || '').toLowerCase()
    const c = an.localeCompare(bn)
    return sortDesc ? -c : c
  })

  function mergeFiles(existing, incoming) {
    const map = new Map()
    const key = (f) => `${f.name}-${f.size}-${f.lastModified}`
    existing.forEach((f) => map.set(key(f), f))
    incoming.forEach((f) => map.set(key(f), f))
    return Array.from(map.values())
  }

  function onFileInputChange(e) {
    const selected = Array.from(e.target.files || [])
    if (selected.length) {
      setPendingFiles((prev) => mergeFiles(prev, selected))
      setStatus(`Selected ${selected.length} file(s) — click Upload to add them to the knowledge base.`)
    }
    e.target.value = ''
  }

  function onDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    const dropped = Array.from(e.dataTransfer?.files || [])
    if (dropped.length) {
      setPendingFiles((prev) => mergeFiles(prev, dropped))
      setStatus(`Selected ${dropped.length} file(s) — click Upload to add them to the knowledge base.`)
    }
  }

  async function upload() {
    if (!pendingFiles.length) return
    setUploading(true)
    setStatus('Uploading files…')
    const fd = new FormData()
    for (const f of pendingFiles) fd.append('files', f)
    try {
      const res = await fetch('/rag/upload', {
        method: 'POST',
        body: fd,
        headers: getAuthHeaders(),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`)
      setStatus(`✅ Uploaded ${data.files.length} file(s), ${data.total_chunks} chunks`)
      setPendingFiles([])
      await loadLibrary()
    } catch (err) {
      setStatus(`❌ Error: ${err.message}`)
    } finally {
      setUploading(false)
    }
  }

  async function confirmDelete(name) {
    setDeleting(name)
    setDeleteConfirm(null)
    try {
      const res = await fetch(`/rag/files?name=${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`)
      setStatus(
        data.removed_chunks
          ? `✅ Removed "${name}" (${data.removed_chunks} chunks)`
          : `✅ Removed "${name}" from the library`
      )
      await loadLibrary()
    } catch (err) {
      setStatus(`❌ Could not remove file: ${err.message}`)
    } finally {
      setDeleting(null)
    }
  }

  const statusVariant = statusMessageVariant(status)
  const libraryCountLabel = libraryLoading ? '…' : sortedLibrary.length
  const libraryFileWord = sortedLibrary.length === 1 ? 'file' : 'files'

  const sortButton = (
    <Button
      type="button"
      variant="ghost"
      className="doc-list-sort-btn"
      onClick={() => setSortDesc((v) => !v)}
      aria-label={sortDesc ? 'Sort file names A to Z' : 'Sort file names Z to A'}
    >
      <i className="fas fa-arrow-up-wide-short" aria-hidden="true" />{' '}
      {sortDesc ? 'Sort Z–A' : 'Sort A–Z'}
    </Button>
  )

  return (
    <>
      <DataPanelCard title="Upload files" paddedBody layout="readable" aria-label="Upload files">
        <div
          className={`doc-dropzone${dragActive ? ' doc-dropzone-active' : ''}`}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              inputRef.current?.click()
            }
          }}
          onClick={() => inputRef.current?.click()}
          onDragEnter={(e) => {
            e.preventDefault()
            setDragActive(true)
          }}
          onDragOver={(e) => {
            e.preventDefault()
            setDragActive(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            if (!e.currentTarget.contains(e.relatedTarget)) setDragActive(false)
          }}
          onDrop={onDrop}
          aria-label="Upload area: drag files here or click to browse"
        >
          <input
            ref={inputRef}
            type="file"
            className="doc-dropzone-input"
            multiple
            accept=".pdf,.txt,.md,.csv,.doc,.docx,.xls,.xlsx,image/*"
            onChange={onFileInputChange}
            aria-label="Select files to upload to the knowledge base"
          />
          <div className="doc-dropzone-icon" aria-hidden="true">
            <i className="fas fa-cloud-arrow-up" />
          </div>
          <div className="doc-dropzone-label">Drag &amp; drop files here, or click to browse</div>
          <div className="doc-dropzone-sub">Uploads are added to your AI knowledge base when you click Upload</div>
          <div className="doc-format-chips" aria-hidden="true">
            <span className="doc-format-chip">
              <i className="fas fa-file-pdf doc-format-chip-icon doc-format-chip-pdf" /> PDF
            </span>
            <span className="doc-format-chip">
              <i className="fas fa-file-lines doc-format-chip-icon doc-format-chip-txt" /> TXT
            </span>
            <span className="doc-format-chip">
              <i className="fas fa-file-word doc-format-chip-icon doc-format-chip-docx" /> DOCX
            </span>
            <span className="doc-format-chip">
              <i className="fas fa-file-csv doc-format-chip-icon doc-format-chip-csv" /> CSV
            </span>
            <span className="doc-format-chip">
              <i className="fas fa-image doc-format-chip-icon doc-format-chip-img" /> Images
            </span>
          </div>
        </div>

        {pendingFiles.length > 0 && (
          <div className="doc-pending-bar">
            <span className="doc-pending-label">
              {pendingFiles.length} file(s) ready —{' '}
              {pendingFiles.map((f) => f.name).join(', ')}
            </span>
            <Button
              id="upload-files-button"
              type="button"
              variant="primary"
              className={uploading ? 'loading' : ''}
              disabled={uploading}
              onClick={(e) => {
                e.stopPropagation()
                upload()
              }}
              aria-label={uploading ? 'Uploading files' : 'Upload selected files'}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        )}

        {status && statusVariant && (
          <StatusPill
            variant={statusVariant}
            className="status-pill--block doc-status-pill"
            onDismiss={statusVariant === 'error' ? () => setStatus('') : undefined}
          >
            {statusMessageText(status)}
          </StatusPill>
        )}
      </DataPanelCard>

      <DataPanelCard
        title={`Document library (${libraryCountLabel} ${libraryFileWord})`}
        headerAction={sortButton}
        paddedBody={false}
        aria-label="Document library"
      >
        {libraryError && (
          <div className="doc-list-message page-alert-error" role="alert">
            {libraryError}
          </div>
        )}

        {!libraryLoading && !libraryError && sortedLibrary.length === 0 && (
          <div className="doc-list-message doc-list-empty">No documents in the library yet.</div>
        )}

        {libraryLoading && <div className="doc-list-message">Loading library…</div>}

        {!libraryLoading && sortedLibrary.length > 0 && (
          <div className="doc-list doc-list--panel">
            {sortedLibrary.map((row) => {
              const name = row.name || ''
              const kind = docKindFromName(name)
              const sizeStr = formatBytes(row.size_bytes)
              const dateStr = row.modified
                ? new Date(row.modified).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })
                : null
              const metaBits = [dateStr ? `Uploaded ${dateStr}` : null, sizeStr].filter(Boolean)
              const meta = metaBits.join(' · ') || 'Indexed for retrieval'
              const [faIcon, faCls] = DOC_ICON_MAP[kind] || DOC_ICON_MAP.other

              return (
                <div key={name} className="doc-item">
                  <i className={`fas ${faIcon} doc-item-icon ${faCls}`} aria-hidden="true" />
                  <div className="doc-info">
                    <div className="doc-name">{name}</div>
                    <div className="doc-meta">{meta}</div>
                  </div>
                  <span className={`doc-type-badge doc-type-badge-${kind === 'docx' ? 'docx' : kind === 'image' ? 'img' : kind}`}>
                    {kind === 'txt'
                      ? 'TXT'
                      : kind === 'docx'
                        ? 'DOCX'
                        : kind === 'image'
                          ? 'IMG'
                          : kind.toUpperCase()}
                  </span>
                  <button
                    type="button"
                    className="doc-delete"
                    aria-label={`Remove ${name} from knowledge base`}
                    disabled={deleting === name}
                    onClick={() => setDeleteConfirm(name)}
                  >
                    <i className="fas fa-trash" aria-hidden="true" />
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </DataPanelCard>

      <DocumentsDeleteModal
        open={!!deleteConfirm}
        fileName={deleteConfirm}
        deleting={deleting === deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => confirmDelete(deleteConfirm)}
      />
    </>
  )
}
